import type { LearningPlanActivity } from '../types/learningPlanActivity';

/**
 * 초등 수업에서 자주 쓰는 활동 구조.
 * 안내는 진행 순서, 쓰기 좋은 상황, 장점, 유의점 순이다.
 * 도입·강의·시범은 학습지도 단계와 직접 교수(설명-시범-질문)를,
 * 조사·실험은 탐구 절차를, 직소·생각짝나누기·갤러리워크·스테이션은 협동 활동 절차를,
 * 정리 활동은 한 줄 정리·출구 확인·자기 점검을 참고해 요약했다.
 */
export const LEARNING_PLAN_ACTIVITY_SEED: LearningPlanActivity[] = [
  {
    id: 'intro-story',
    phase: 'intro',
    name: '이야기 도입',
    method: '전체',
    guide: [
      '1. 오늘 배울 내용과 닿은 짧은 이야기나 일화를 고른다. 인물, 장소, 문제가 학습 내용과 연결되어야 한다.',
      '2. 결말은 말하지 않고 1분 안에 들려준 뒤 중간에서 멈춘다.',
      '3. 이야기에서 궁금한 점을 묻고, 그중 하나를 오늘 학습 문제로 잇는다.',
      '이런 때: 새 단원을 열거나, 배울 내용이 생활과 멀어 보일 때.',
      '장점: 주의가 모이고 왜 배우는지가 이야기로 남는다.',
      '유의: 이야기가 길거나 학습 내용과 어긋나면 흥미만 남는다.',
    ].join('\n'),
    sort: 0,
  },
  {
    id: 'intro-material',
    phase: 'intro',
    name: '자료 제시',
    method: '전체',
    guide: [
      '1. 오늘 내용이 보이는 사진, 실물, 영상 중 하나를 고른다.',
      '2. 짧게 보여주고, 볼 포인트를 한 가지만 말한다.',
      '3. 본 것을 말한 뒤 그 관찰을 학습 문제로 잇는다.',
      '이런 때: 현상, 장면, 실물을 먼저 봐야 질문이 생기는 수업.',
      '장점: 설명 전에 모두가 같은 것을 본 상태가 된다.',
      '유의: 자료를 오래 틀거나 포인트를 여러 개 주면 주의가 흩어진다.',
    ].join('\n'),
    sort: 1,
  },
  {
    id: 'intro-question',
    phase: 'intro',
    name: '질문 던지기',
    method: '전체',
    guide: [
      '1. 답이 하나로 닫히지 않는 질문을 학습 내용에서 하나 정한다.',
      '2. 질문을 적어 보여 주고, 바로 발표시키지 않은 채 생각할 시간을 준다.',
      '3. 두세 개의 답을 듣고, 오늘 확인할 질문을 하나 고른다.',
      '이런 때: 학생의 생각과 궁금증을 먼저 꺼내야 하는 도입.',
      '장점: 오개념과 궁금증이 보여 수업 방향을 잡기 쉽다.',
      '유의: 정답을 바로 말하거나 손을 든 학생만 시키면 질문이 닫힌다. 대답 전에 잠시 기다린다.',
    ].join('\n'),
    sort: 2,
  },
  {
    id: 'intro-experience',
    phase: 'intro',
    name: '경험 연결',
    method: '전체 · 짝',
    guide: [
      '1. 오늘 내용과 닮은 생활 경험을 교사가 한 가지 먼저 말한다.',
      '2. 짝과 짧은 시간 동안 비슷한 경험 한 가지를 나눈다.',
      '3. 나온 경험을 오늘 배울 말이나 개념과 한 문장으로 잇는다.',
      '이런 때: 일상 경험 위에 교과 내용을 붙여야 할 때.',
      '장점: 이미 아는 경험 위에 새 내용을 얹어 이해가 쉽다.',
      '유의: 경험 이야기가 길어지면 본 활동으로 들어가지 못한다. 경험은 한 가지로 제한한다.',
    ].join('\n'),
    sort: 3,
  },
  {
    id: 'intro-prior',
    phase: 'intro',
    name: '사전 지식 확인',
    method: '전체',
    guide: [
      '1. 이미 아는 것과 아직 모르는 것을 짧게 적거나 말하게 한다.',
      '2. 아는 것 가운데 오늘 내용과 이어지는 것, 달라 보이는 것을 고른다.',
      '3. 모르는 것 하나를 오늘 해결할 학습 문제로 적는다.',
      '이런 때: 앞 차시와 이어지거나 학생마다 배경지식이 다를 때.',
      '장점: 빈칸과 오개념을 수업 전에 볼 수 있다.',
      '유의: 맞다 틀리다로 평가하지 않는다. 모른다고 말해도 괜찮은 분위기여야 한다.',
    ].join('\n'),
    sort: 4,
  },
  {
    id: 'intro-demo',
    phase: 'intro',
    name: '시범 보이기',
    method: '전체',
    guide: [
      '1. 학생이 따라 할 절차를 교사가 천천히 한 번 해 보이며, 그때 생각하는 말을 소리 내어 말한다.',
      '2. 지금 무엇을 봤는지 한 가지만 묻는다.',
      '3. 같은 절차를 학생 한 명이나 전체가 짧게 따라 한 뒤 본활동으로 넘긴다.',
      '이런 때: 절차, 기능, 조작이 새롭거나 어려울 때.',
      '장점: 무엇을 어떻게 할지 보여 주어 시작이 빠르다.',
      '유의: 시범이 길면 학생이 해 볼 시간이 없다. 한 절차만 보여 주고, 구경으로 끝나지 않게 바로 따라 하게 한다.',
    ].join('\n'),
    sort: 5,
  },
  {
    id: 'learn-lecture',
    phase: 'learn',
    name: '강의',
    method: '전체 · 강의',
    guide: [
      '1. 오늘 꼭 알아야 할 핵심을 한두 가지로 줄여 말한다.',
      '2. 예시 하나로 그 핵심이 어디에 있는지 보여 준다.',
      '3. 바로 확인 질문을 하고, 어긋난 답이 나오면 그 부분만 다시 말한다.',
      '이런 때: 개념, 용어, 절차를 먼저 맞춰야 다음 활동이 될 때.',
      '장점: 모두가 같은 설명을 듣고 활동의 기준이 생긴다.',
      '유의: 설명이 길어지면 참여가 끊긴다. 길지 않은 설명 뒤에 학생 반응을 확인한다.',
    ].join('\n'),
    sort: 0,
  },
  {
    id: 'learn-inquiry',
    phase: 'learn',
    name: '조사활동',
    method: '모둠활동 · 조사활동',
    guide: [
      '1. 조사할 질문, 볼 자료, 적을 항목을 먼저 정한다. 인터넷은 교사가 고른 자료만 연다.',
      '2. 자료에서 질문의 답에 해당하는 문장, 숫자, 장면만 찾아 항목에 적는다.',
      '3. 출처와 함께 한 문장으로 정리하고, 모둠이 같은 내용인지 확인한다.',
      '이런 때: 교과서 밖에 사실, 사례, 자료가 필요할 때.',
      '장점: 자료를 읽고 필요한 정보만 고르는 연습이 된다.',
      '유의: 질문과 기록 칸이 없으면 베끼기로 끝난다. 검색창을 그대로 열어 두지 않는다.',
    ].join('\n'),
    sort: 1,
  },
  {
    id: 'learn-discussion',
    phase: 'learn',
    name: '토론활동',
    method: '모둠활동 · 토론활동',
    guide: [
      '1. 토론 질문, 말할 순서, 한 사람이 말하는 시간을 정한다.',
      '2. 각자 주장 한 문장과 근거 한 가지를 적고 돌아가며 말한다. 상대는 근거의 빈칸을 묻는다.',
      '3. 모둠 의견을 한 문장으로 적고, 아직 갈리는 점을 따로 적는다.',
      '이런 때: 답이 여러 개이거나 근거를 비교해야 할 때.',
      '장점: 주장과 근거를 구분하고 다른 생각을 듣게 된다.',
      '유의: 큰 소리나 사람 수가 결론이 되지 않게 근거를 적게 한다. 역할이 없으면 한 사람만 말한다.',
    ].join('\n'),
    sort: 2,
  },
  {
    id: 'learn-gallery',
    phase: 'learn',
    name: '갤러리워크',
    method: '모둠활동 · 갤러리워크',
    guide: [
      '1. 모둠 결과물을 붙이고, 보고 답할 질문을 하나 적는다.',
      '2. 다른 모둠 결과로 옮겨 질문의 답을 메모로 남긴다. 신호가 나면 다음으로 이동한다.',
      '3. 자기 자리로 돌아와 붙은 메모를 읽고, 고칠 점 하나를 반영한다.',
      '이런 때: 모둠 결과물을 서로 비교하고 피드백할 때.',
      '장점: 긴 발표 없이도 여러 결과물을 볼 수 있다.',
      '유의: 볼 질문이 없으면 구경으로 끝난다. 한 곳에 오래 머물지 않게 시간을 알린다.',
    ].join('\n'),
    sort: 3,
  },
  {
    id: 'learn-pair',
    phase: 'learn',
    name: '짝 설명',
    method: '짝활동',
    guide: [
      '1. 설명할 내용과 채울 빈칸을 정한다.',
      '2. 한 사람이 설명하고 짝은 막히는 곳에서 질문한다. 그다음 역할을 바꾼다.',
      '3. 둘 다 막힌 빈칸만 표시해 전체에 묻는다.',
      '이런 때: 설명을 들은 뒤 이해했는지 확인하거나 말로 정리해야 할 때.',
      '장점: 설명하는 쪽이 빈칸을 알고, 조용한 학생도 한 번 말한다.',
      '유의: 답을 읽어 주는 활동이 되지 않게 이유를 묻게 한다.',
    ].join('\n'),
    sort: 4,
  },
  {
    id: 'learn-individual',
    phase: 'learn',
    name: '개별 정리',
    method: '개별활동',
    guide: [
      '1. 혼자 할 문제나 정리 칸을 주고, 상의하지 않는 시간을 알린다.',
      '2. 풀거나 적는다. 교사는 돌아다니며 막힌 곳만 짧게 묻는다.',
      '3. 자신이 쓴 것에서 다시 볼 곳 한 군데를 표시한다.',
      '이런 때: 개인 도달을 확인하거나, 모둠 활동 뒤에 자기 생각을 고정할 때.',
      '장점: 누가 할 수 있는지 개인 기록이 남는다.',
      '유의: 과제가 크면 시간 안에 못 끝낸다. 문제나 칸은 하나로 줄인다.',
    ].join('\n'),
    sort: 5,
  },
  {
    id: 'learn-experiment',
    phase: 'learn',
    name: '실험·실습',
    method: '모둠활동 · 실험·실습',
    guide: [
      '1. 바꿀 조건 하나, 같게 둘 조건, 안전 수칙, 기록 칸을 먼저 확인한다.',
      '2. 절차대로 조작하고 본 것을 바로 적는다. 가능하면 한 번 더 해 기록을 비교한다.',
      '3. 기록으로 알게 된 점을 한 문장 쓴다.',
      '이런 때: 직접 조작해야 결과가 보이는 내용일 때.',
      '장점: 예상과 결과를 비교하며 개념이 남는다.',
      '유의: 안전 수칙과 기록 칸이 없으면 놀이로 끝난다. 한 번에 조건을 여러 개 바꾸지 않는다.',
    ].join('\n'),
    sort: 6,
  },
  {
    id: 'learn-role',
    phase: 'learn',
    name: '역할극',
    method: '모둠활동 · 역할극',
    guide: [
      '1. 상황과 역할을 나누고, 각 역할이 지킬 입장 한 줄을 적는다.',
      '2. 짧게 연습한 뒤 실연한다. 보는 사람은 입장이 드러나는 말을 메모한다.',
      '3. 실연 뒤 그 입장이 된 이유와 달라진 생각을 나눈다.',
      '이런 때: 인물의 입장, 갈등, 사회적 쟁점을 이해해야 할 때.',
      '장점: 다른 사람의 입장에서 말하고 판단해 본다.',
      '유의: 지식의 구조를 익히는 활동으로는 약하다. 대본을 길게 쓰지 않고 핵심 대사만 정한다.',
    ].join('\n'),
    sort: 7,
  },
  {
    id: 'learn-jigsaw',
    phase: 'learn',
    name: '직소',
    method: '모둠활동 · 직소',
    guide: [
      '1. 본모둠에서 내용을 나누고, 같은 부분을 맡은 사람이 전문가 모둠에서 그 부분만 익혀 설명 문장을 만든다.',
      '2. 본모둠으로 돌아와 자기 부분을 설명한다. 다른 사람은 질문하고 빈칸을 채운다.',
      '3. 모둠이 전체를 한 장으로 맞춘 뒤, 각자 다른 사람의 부분을 한 문장으로 말한다.',
      '이런 때: 내용이 나뉘어 있고 서로 가르쳐야 전체가 완성될 때.',
      '장점: 맡은 부분을 알아야 모둠이 완성되므로 책임이 생긴다.',
      '유의: 조각이 길거나 이동이 복잡하면 설명할 시간이 없다. 전문가 과제는 짧게 자른다.',
    ].join('\n'),
    sort: 8,
  },
  {
    id: 'learn-tps',
    phase: 'learn',
    name: '생각-짝-나누기',
    method: '개별활동 · 짝활동',
    guide: [
      '1. 질문을 듣고 혼자 적는다. 이 동안에는 말하지 않는다.',
      '2. 짝에게 적은 것을 말하고, 같은 점과 다른 점을 고른다.',
      '3. 짝의 생각을 전체에 한 문장으로 전한다.',
      '이런 때: 발표 전에 생각을 만들 시간이 필요할 때.',
      '장점: 먼저 말한 사람만 답이 되지 않고 모두가 한 번 말한다.',
      '유의: 혼자 생각할 시간을 빼면 짝의 답을 베끼게 된다.',
    ].join('\n'),
    sort: 9,
  },
  {
    id: 'learn-station',
    phase: 'learn',
    name: '스테이션',
    method: '모둠활동 · 스테이션',
    guide: [
      '1. 자리마다 다른 과제와 하는 방법을 붙인다. 한 자리에서 할 일은 한 가지로 적는다.',
      '2. 신호가 나면 다음 자리로 옮겨 과제를 하고 기록을 남긴다.',
      '3. 자리를 모두 돈 뒤, 남긴 기록에서 공통점 하나를 적는다.',
      '이런 때: 같은 시간에 여러 자료나 과제를 짧게 경험할 때.',
      '장점: 이동하며 여러 각도를 보고, 기다리는 모둠이 적다.',
      '유의: 자리 안내가 길면 활동 시간이 줄어든다. 자리 수와 시간을 미리 적는다.',
    ].join('\n'),
    sort: 10,
  },
  {
    id: 'learn-make',
    phase: 'learn',
    name: '만들기',
    method: '모둠활동 · 만들기',
    guide: [
      '1. 완성물이 갖출 기준, 재료, 순서를 확인한다.',
      '2. 기준에 맞춰 만든다. 막히면 기준의 어느 항목에서 막혔는지 말한다.',
      '3. 기준 항목으로 스스로 점검하고, 고칠 곳 하나를 고친다.',
      '이런 때: 개념을 형태나 작품으로 나타내야 할 때.',
      '장점: 만든 결과로 이해한 정도가 보인다.',
      '유의: 기준이 없으면 꾸미기로 끝난다. 예쁜지보다 기준을 맞췄는지 본다.',
    ].join('\n'),
    sort: 11,
  },
  {
    id: 'wrap-line',
    phase: 'wrap',
    name: '한 줄 정리',
    method: '개별활동',
    guide: [
      '1. 오늘 가장 중요한 것을 한 문장으로 적는다.',
      '2. 두세 문장을 함께 듣는다.',
      '3. 자신의 문장에 학습 내용이 없으면 핵심 말을 한 단어 보탠다.',
      '이런 때: 수업 끝에 핵심을 스스로 말해야 할 때.',
      '장점: 짧아서 모두가 쓰고, 이해의 빈칸이 보인다.',
      '유의: 재미있었다는 식의 문장은 다시 쓰게 한다. 오늘 배운 말이 들어가야 한다.',
    ].join('\n'),
    sort: 0,
  },
  {
    id: 'wrap-exit',
    phase: 'wrap',
    name: '출구 확인',
    method: '개별활동',
    guide: [
      '1. 오늘 목표와 맞는 확인 질문 하나를 준다.',
      '2. 혼자 답하고 제출한다.',
      '3. 많이 어긋난 답을 보고, 다음 수업 시작에서 그 부분만 다시 다룬다.',
      '이런 때: 오늘 목표에 도달했는지 개인별로 볼 때.',
      '장점: 수업이 끝난 뒤에도 누구의 이해가 비었는지 남는다.',
      '유의: 질문을 여러 개 주면 출구가 시험이 된다. 질문은 하나로 두고, 결과는 다음 수업의 출발로 쓴다.',
    ].join('\n'),
    sort: 1,
  },
  {
    id: 'wrap-share',
    phase: 'wrap',
    name: '결과 나누기',
    method: '전체',
    guide: [
      '1. 결과물을 모두가 보게 게시한다.',
      '2. 결과에서 학습 내용이 드러나는 부분만 한 문장으로 말한다.',
      '3. 듣는 사람은 배운 점 하나를 말하고, 그 말을 게시된 결과와 잇는다.',
      '이런 때: 활동 결과를 공유하고 마무리할 때.',
      '장점: 결과와 배운 점이 함께 남아 전시만으로 끝나지 않는다.',
      '유의: 모두 발표하면 시간이 넘는다. 말할 모둠 수와 문장 수를 제한한다.',
    ].join('\n'),
    sort: 2,
  },
  {
    id: 'wrap-check',
    phase: 'wrap',
    name: '자기 점검',
    method: '개별활동',
    guide: [
      '1. 오늘 할 수 있어야 하는 것을 두세 칸으로 보여 준다.',
      '2. 할 수 있음과 아직 어려움으로 표시하고, 어려운 칸의 이유를 한 줄 적는다.',
      '3. 다음에 다시 볼 것 하나를 고른다.',
      '이런 때: 활동 뒤 스스로 도달을 돌아볼 때.',
      '장점: 학생은 빈칸을, 교사는 다음에 도울 곳을 안다.',
      '유의: 잘함으로만 표시하지 않게 이유 한 줄을 받는다. 점수로 쓰지 않는다고 말한다.',
    ].join('\n'),
    sort: 3,
  },
  {
    id: 'wrap-next',
    phase: 'wrap',
    name: '다음 질문',
    method: '개별활동',
    guide: [
      '1. 오늘 배운 것 가운데 아직 궁금한 것을 한 가지 적는다.',
      '2. 비슷한 질문끼리 모아 읽는다.',
      '3. 다음 시간에 다룰 질문 하나를 정하고, 오늘 내용과 어떻게 이어지는지 한 문장으로 말한다.',
      '이런 때: 다음 차시를 오늘 배움과 연결해 예고할 때.',
      '장점: 마무리가 다음 학습 문제가 된다.',
      '유의: 수업과 먼 호기심으로 새지 않게, 오늘 내용에서 나온 질문만 받는다.',
    ].join('\n'),
    sort: 4,
  },
];
